import { Button } from "@prelude-ai/design-system"
import { createFileRoute, Link, useLoaderData, useNavigate, useSearch } from "@tanstack/react-router"
import { useCallback, useMemo, useRef, useState } from "react"
import ChatInterface from "../components/ChatInterface.tsx"
import FlowStateEditor, { createEntry, type FlowStateEntry, parseEntryValue } from "../components/FlowStateEditor.tsx"
import { API_BASE_URL } from "../config.ts"
import { usePendingMessages } from "../contexts/PendingMessageContext.tsx"
import type { StagedAttachment } from "../types.ts"
import { toast } from "../utils/toast.tsx"
import { uploadFiles } from "../utils/upload-files.ts"

export const Route = createFileRoute("/new")({
  component: App
})

function App() {
  const {
    agent: selectedAgent,
    devTools: devtoolsParam,
    initialFlowContext: initialFlowContextParam
  } = useSearch({ from: "/new" })
  const { agents } = useLoaderData({ from: "__root__" })
  const [input, setInput] = useState("")
  const navigate = useNavigate({ from: "/new" })
  const { setMessages } = usePendingMessages()
  const [attachments, setAttachments] = useState<StagedAttachment[]>([])
  const [flowContextEntries, setFlowContextEntries] = useState<FlowStateEntry[]>(() => {
    if (!initialFlowContextParam) return [createEntry()]
    const parsed = JSON.parse(initialFlowContextParam) as { key: string; value: string }[]
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed.map((e) => createEntry(e.key, e.value))
    }
    return [createEntry()]
  })
  const uploadConversationIdRef = useRef<{ agentName: string; conversationId: string } | undefined>(undefined)

  const parsedFlowContext = useMemo<Record<string, unknown>>(() => {
    const result: Record<string, unknown> = {}
    for (const entry of flowContextEntries) {
      const key = entry.key.trim()
      if (key) {
        result[key] = parseEntryValue(entry.value)
      }
    }
    return result
  }, [flowContextEntries])

  const handleFlowContextChange = useCallback(
    (entries: FlowStateEntry[]) => {
      setFlowContextEntries(entries)
      const nonEmpty = entries.filter((e) => e.key.trim())
      const serialized =
        nonEmpty.length > 0 ? JSON.stringify(nonEmpty.map((e) => ({ key: e.key, value: e.value }))) : undefined
      navigate({ to: "/new", search: (prev) => ({ ...prev, initialFlowContext: serialized }), replace: true })
    },
    [navigate]
  )

  const handleCloseDevTools = useCallback(() => {
    navigate({ to: "/new", search: (prev) => ({ ...prev, devTools: undefined }) })
  }, [navigate])

  const createConversation = useCallback(async () => {
    if (!selectedAgent) return undefined

    const flowContext = Object.keys(parsedFlowContext).length > 0 ? parsedFlowContext : undefined

    try {
      const response = await fetch(`${API_BASE_URL}/api/conversations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          agentName: selectedAgent,
          transcript: [],
          ...(flowContext && { flowContext })
        })
      })

      if (!response.ok) {
        toast.error("Failed to create conversation", {
          description: `POST /api/conversations returned ${response.status} for agent "${selectedAgent}"`
        })
        return undefined
      }

      const data = await response.json()
      return data?.conversationId as string | undefined
    } catch (error) {
      toast.error("Failed to create conversation", {
        description: `POST /api/conversations threw for agent "${selectedAgent}": ${error instanceof Error ? error.message : "Network error"}`
      })
      return undefined
    }
  }, [parsedFlowContext, selectedAgent])

  const uploadFile = useCallback(
    async (files: File[]) => {
      if (!selectedAgent) return

      const pendingUploadConversation = uploadConversationIdRef.current
      const conversationId =
        pendingUploadConversation?.agentName === selectedAgent
          ? pendingUploadConversation.conversationId
          : await createConversation()
      if (!conversationId) return
      uploadConversationIdRef.current = { agentName: selectedAgent, conversationId }

      const result = await uploadFiles({
        files,
        agentName: selectedAgent,
        conversationId,
        setAttachments
      })
      if (result.conversationId && uploadConversationIdRef.current?.agentName === selectedAgent) {
        uploadConversationIdRef.current = { agentName: selectedAgent, conversationId: result.conversationId }
      }
    },
    [createConversation, selectedAgent]
  )

  const isUploading = attachments.some((a) => a.status === "uploading")

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id))
  }, [])

  const handleOnSend = useCallback(async () => {
    if ((!input.trim() && attachments.length === 0) || !selectedAgent) return

    const attachmentIds = attachments.map((a) => a.id)
    const pendingUploadConversation = uploadConversationIdRef.current
    const conversationId =
      pendingUploadConversation?.agentName === selectedAgent
        ? pendingUploadConversation.conversationId
        : await createConversation()

    if (conversationId) {
      setMessages([{ text: input.trim(), attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined }])
      setAttachments([])
      uploadConversationIdRef.current = undefined
      navigate({
        to: "/conversations/$conversationId",
        params: { conversationId },
        search: (prev) => ({
          ...prev,
          agent: selectedAgent,
          devTools: devtoolsParam,
          initialFlowContext: initialFlowContextParam
        }),
        state: { fresh: true }
      })
    }
  }, [
    input,
    attachments,
    selectedAgent,
    navigate,
    setMessages,
    createConversation,
    devtoolsParam,
    initialFlowContextParam
  ])

  if (!selectedAgent) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="border-2 border-muted-foreground/30 rounded p-6 sm:p-8 font-mono text-center space-y-4">
          <pre className="text-muted-foreground text-xs sm:text-sm leading-tight">
            {[
              "\u2591\u2588\u2580\u2588\u2591\u2588\u2580\u2580\u2591\u2588\u2580\u2580\u2591\u2588\u2580\u2588\u2591\u2580\u2588\u2580\u2591\u2588\u2580\u2580",
              "\u2591\u2588\u2580\u2588\u2591\u2588\u2591\u2588\u2591\u2588\u2580\u2580\u2591\u2588\u2591\u2588\u2591\u2591\u2588\u2591\u2591\u2580\u2580\u2588",
              "\u2591\u2580\u2591\u2580\u2591\u2580\u2580\u2580\u2591\u2580\u2580\u2580\u2591\u2580\u2591\u2580\u2591\u2591\u2580\u2591\u2591\u2580\u2580\u2580"
            ].join("\n")}
          </pre>
          <p className="text-muted-foreground text-sm">{"> SELECT AN AGENT TO BEGIN_"}</p>
          <p className="text-muted-foreground/60 text-xs">
            Use the dropdown above to pick an
            <br />
            agent and start a new conversation.
          </p>
          {agents.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 pt-2">
              {agents.map((a) => (
                <Button key={a.name} variant="outline" size="sm" className="font-mono text-xs" asChild>
                  <Link to="/new" search={(prev) => ({ ...prev, agent: a.name })}>
                    {a.name}
                  </Link>
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <ChatInterface
      input={input}
      setInput={setInput}
      flowState={parsedFlowContext}
      currentResponse={""}
      onSend={handleOnSend}
      stepPath={[]}
      stepHistory={[]}
      // no-op since this just for the new conversation
      onSubmitClarification={() => {}}
      onCloseClarification={() => {}}
      currentThought={null}
      devToolsMode={devtoolsParam}
      devToolsInitialTab="starting-state"
      startingFlowStateEditor={
        devtoolsParam ? (
          <FlowStateEditor
            entries={flowContextEntries}
            onChange={handleFlowContextChange}
            title="Initial Flow Context"
          />
        ) : undefined
      }
      onCloseDevTools={handleCloseDevTools}
      attachments={attachments}
      onAttach={uploadFile}
      onRemoveAttachment={removeAttachment}
      isUploading={isUploading}
    />
  )
}
